import React from 'react';
import './UIThreeColumnWrapper.scss';

export interface UIThreeColumnWrapperProps {
  /**
   * Три дочірні елементи для колонок
   */
  children: [React.ReactNode, React.ReactNode, React.ReactNode];

  /**
   * Варіант spacing між колонками
   */
  gap?: 'small' | 'medium' | 'large';

  /**
   * Варіант padding контейнера
   */
  spacing?: 'tight' | 'compact' | 'default' | 'spacious';

  /**
   * Вирівнювання колонок по висоті
   */
  equalHeight?: boolean;

  /**
   * Layout на мобільних пристроях
   */
  mobileLayout?: 'stack' | 'wrap';

  /**
   * Варіант тіні
   */
  shadow?: 'none' | 'default' | 'light' | 'medium' | 'strong';

  /**
   * Варіант фону
   */
  background?: 'white' | 'transparent' | 'light' | 'gray';

  /**
   * Варіант рамки
   */
  border?: 'none' | 'default' | 'primary';

  /**
   * Інтерактивні ефекти
   */
  interactive?: 'none' | 'hoverable' | 'clickable';

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Обробник кліку (тільки для clickable варіанта)
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * HTML атрибути
   */
  [key: string]: any;
}

// Моковые данные для демонстрації
const mockProps: Partial<UIThreeColumnWrapperProps> = {
  gap: 'medium',
  spacing: 'default',
  equalHeight: true,
  mobileLayout: 'stack',
  shadow: 'default',
  background: 'white',
  border: 'none',
  interactive: 'none',
};

const baseClass = 'UIThreeColumnWrapper';

const UIThreeColumnWrapper: React.FC<UIThreeColumnWrapperProps> = ({
                                                                     children,
                                                                     gap = mockProps.gap,
                                                                     spacing = mockProps.spacing,
                                                                     equalHeight = mockProps.equalHeight,
                                                                     mobileLayout = mockProps.mobileLayout,
                                                                     shadow = mockProps.shadow,
                                                                     background = mockProps.background,
                                                                     border = mockProps.border,
                                                                     interactive = mockProps.interactive,
                                                                     className = '',
                                                                     onClick,
                                                                     ...props
                                                                   }) => {
  // Перевіряємо що передано рівно 3 елементи
  if (!Array.isArray(children) || children.length !== 3) {
    console.warn('UIThreeColumnWrapper: Expected exactly 3 children');
    return null;
  }

  const containerClasses = [
    baseClass,
    // Gap
    gap !== 'medium' && `${baseClass}--gap-${gap}`,
    // Spacing
    spacing !== 'default' && `${baseClass}--${spacing}`,
    // Equal height
    equalHeight && `${baseClass}--equal-height`,
    // Mobile layout
    mobileLayout !== 'stack' && `${baseClass}--mobile-${mobileLayout}`,
    // Shadow
    shadow === 'none' && `${baseClass}--no-shadow`,
    shadow === 'light' && `${baseClass}--light-shadow`,
    shadow === 'medium' && `${baseClass}--medium-shadow`,
    shadow === 'strong' && `${baseClass}--strong-shadow`,
    // Background
    background === 'transparent' && `${baseClass}--transparent`,
    background === 'light' && `${baseClass}--light`,
    background === 'gray' && `${baseClass}--gray`,
    // Border
    border === 'default' && `${baseClass}--bordered`,
    border === 'primary' && `${baseClass}--primary-border`,
    // Interactive
    interactive === 'hoverable' && `${baseClass}--hoverable`,
    interactive === 'clickable' && `${baseClass}--clickable`,
    // Custom
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (interactive === 'clickable' && onClick) {
      onClick(event);
    }
  };

  return (
    <div
      className={containerClasses}
      onClick={handleClick}
      role={interactive === 'clickable' ? 'button' : undefined}
      tabIndex={interactive === 'clickable' ? 0 : undefined}
      {...props}
    >
      <div className={`${baseClass}__columns`}>
        <div className={`${baseClass}__column`}>
          {children[0]}
        </div>
        <div className={`${baseClass}__column`}>
          {children[1]}
        </div>
        <div className={`${baseClass}__column`}>
          {children[2]}
        </div>
      </div>
    </div>
  );
};

export default UIThreeColumnWrapper;